import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FiPlus,
  FiEdit,
  FiTrash2,
  FiPower,
  FiKey,
  FiLock,
  FiUserCheck,
  FiUserX,
  FiCheckCircle,
  FiXCircle,
  FiChevronRight,
  FiChevronLeft,
  FiSettings,
} from "react-icons/fi";

import AdminContainer from "../../../components/utils/AdminContainer";
import BandModal from "../../../components/utils/BandModal";
import DashboardAccessModals from "../../../components/utils/DashboardAccessModals.js";
import Loader from "../../../components/utils/Loader";

import {
  getAdminBands,
  createBand,
  editBand,
  deleteBand,
  toggleBandStatus,
  createDashboardAccess,
  resetDashboardPassword,
  removeDashboardAccess,
  fetchAdminCredentials,
} from "../../../store/slices/bands/asyncThunks";

import {
  selectBands,
  selectDashboardAccess,
  clearBandsError,
  clearDashboardAccessError,
  selectAdminCredentials,
} from "../../../store/slices/bands/slice";

import {
  PageContainer,
  PageHeader,
  PageTitle,
  Button,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  StatusBadge,
  ActionButton,
  TableContainer,
  SearchContainer,
  SearchInput,
  Buttons,
  CenteredContainer,
  PaginationContainer,
  PaginationInfo,
  PaginationButton,
  ModalOverlay,
  ConfirmBox,
  CloseBtn,
  ModalActions,
  Cancel,
  Confirm,
} from "./AdminBandsPage.styles";

import { useNavigate } from "react-router-dom";
import { toast } from "../../../components/modern-ui/sonner.js";
import { AnimatePresence, motion } from "framer-motion";
import { FaTimes } from "react-icons/fa";

const AdminBandsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux selectors
  const { data: bands, loading, error, pagination } = useSelector(selectBands);
  const dashboardAccess = useSelector(selectDashboardAccess);
  const { data: adminCredentials, loading: adminCredentialsLoading } =
    useSelector(selectAdminCredentials);

  useEffect(() => {
    if (bands.length > 0) {
      bands.forEach((band) => {
        dispatch(fetchAdminCredentials(band.id));
      });
    }
  }, [bands, dispatch]);

  // Local state
  const [activeModal, setActiveModal] = useState(null);
  const [selectedBand, setSelectedBand] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
    confirmText: "Da",
    cancelText: "Ne",
  });

  const [currentPage, setCurrentPage] = useState(1);

  // Load bands on mount and when page changes
  useEffect(() => {
    dispatch(getAdminBands({ page: currentPage, limit: 10, search: searchTerm }));
  }, [dispatch, currentPage, searchTerm]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearBandsError());
      dispatch(clearDashboardAccessError());
    };
  }, [dispatch]);

  // Modal handlers
  const openModal = (modal, band = null) => {
    setActiveModal(modal);
    setSelectedBand(band);
  };

  const closeModal = () => {
    setActiveModal(null);
    setSelectedBand(null);
  };

  // Confirmation modal handlers
  const openConfirmModal = (
    title,
    message,
    onConfirm,
    confirmText = "Da",
    cancelText = "Ne"
  ) => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      onConfirm,
      confirmText,
      cancelText,
    });
  };

  const closeConfirmModal = () => {
    setConfirmModal({
      isOpen: false,
      title: "",
      message: "",
      onConfirm: null,
      confirmText: "Da",
      cancelText: "Ne",
    });
  };

  const handleConfirm = () => {
    if (confirmModal.onConfirm) {
      confirmModal.onConfirm();
    }
    closeConfirmModal();
  };

  // Band CRUD handlers
  const handleBandSubmit = async (bandData) => {
    try {
      if (activeModal === "editBand") {
        await dispatch(
          editBand({ id: selectedBand.id, requestData: bandData })
        ).unwrap();

        toast.success("Bend je uspešno ažuriran!");
      } else {
        await dispatch(createBand(bandData)).unwrap();

        toast.success("Bend je uspešno kreiran!");
      }
      closeModal();
      // Refresh data
      dispatch(getAdminBands({ page: currentPage, limit: 10, search: searchTerm }));
    } catch (error) {
      console.error("Error submitting band:", error);
      throw error; // Proslijedi error modal-u
    }
  };

  const handleDeleteBand = async (bandId) => {
    const performDelete = async () => {
      try {
        await dispatch(deleteBand(bandId)).unwrap();

        toast.success("Bend je uspešno obrisan!");
      } catch (error) {
        toast.error("Greška pri brisanju benda: " + error.message);
      }
    };

    openConfirmModal(
      "Potvrdi brisanje",
      "Da li ste sigurni da želite da obrišete ovaj bend?",
      performDelete,
      "Obriši",
      "Otkaži"
    );
  };

  const handleToggleStatus = async (bandId) => {
    try {
      await dispatch(toggleBandStatus(bandId)).unwrap();
    } catch (error) {
      toast.error("Greška pri menjanju statusa: " + error.message);
    }
  };

  // DASHBOARD ACCESS HANDLERS

  const handleCreateDashboardAccess = async (bandId, accessData) => {
    try {
      await dispatch(
        createDashboardAccess({
          bandId,
          username: accessData.username,
          password: accessData.password,
        })
      ).unwrap();
      toast.success(
        `Dashboard pristup je uspešno kreiran!\nUsername: ${accessData.username}`
      );

      closeModal();

      // Refresh data  
      dispatch(getAdminBands({ page: currentPage, limit: 10, search: searchTerm }));
    } catch (error) {
      console.error("Error creating dashboard access:", error);
      throw error; // Proslijedi error modal-u
    }
  };

  const handleResetPassword = async (bandId, newPassword) => {
    try {
      await dispatch(resetDashboardPassword({ bandId, newPassword }))
        .unwrap()
        .then(() => {
          toast.success("Password je uspešno resetovan!");
          closeModal();
        });
    } catch (error) {
      console.error("Error resetting password:", error);
      throw error; // Proslijedi error modal-u
    }
  };

  const handleRemoveAccess = async (bandId) => {
    const performRemoveAccess = async () => {
      try {
        await dispatch(removeDashboardAccess(bandId))
          .unwrap()
          .then(() => {
            toast.success("Dashboard pristup je uspešno uklonjen!");
            dispatch(fetchAdminCredentials(bandId));
          });

        // Refresh data
      } catch (error) {
        toast.error("Greška pri uklanjanju pristupa: " + error.message);
      }
    };

    openConfirmModal(
      "Ukloni pristup",
      "Da li ste sigurni da želite da uklonite dashboard pristup za ovaj bend?",
      performRemoveAccess,
      "Ukloni",
      "Otkaži"
    );
  };

  // Use bands directly since server handles filtering
  const filteredBands = bands;

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < pagination?.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <AdminContainer>
      <PageContainer>
        <PageHeader>
          <PageTitle>Upravljanje Bendovima</PageTitle>
          <Button
            variant="primary"
            onClick={() => openModal("addBand")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <FiPlus /> Dodaj Bend
          </Button>
        </PageHeader>

        {/* Search */}
        <SearchContainer>
          <SearchInput
            type="text"
            placeholder="Pretraži bendove..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>

        {/* Error handling */}
        {error && (
          <div style={{ color: "#e74c3c", margin: "1rem 0" }}>
            Greška: {error}
          </div>
        )}

        {dashboardAccess.error && (
          <div style={{ color: "#e74c3c", margin: "1rem 0" }}>
            Greška sa dashboard pristupom: {dashboardAccess.error}
          </div>
        )}

        {/* Table */}
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <TableHeader>Naziv</TableHeader>
                <TableHeader>Žanr</TableHeader>
                <TableHeader>Grad</TableHeader>
                <TableHeader>Godina</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Dashboard Pristup</TableHeader>
                <TableHeader>Akcije</TableHeader>
              </tr>
            </thead>
            <tbody>
              {loading || adminCredentialsLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    style={{ textAlign: "center", padding: "2rem" }}
                  >
                    <Loader title="bendova" />
                  </TableCell>
                </TableRow>
              ) : filteredBands.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    style={{ textAlign: "center", padding: "2rem" }}
                  >
                    {searchTerm ? "Nema rezultata pretrage" : "Nema bendova"}
                  </TableCell>
                </TableRow>
              ) : (
                filteredBands.map((band) => {
                  const dashboardStatus = adminCredentials[band?.id];

                  return (
                    <TableRow key={band.id}>
                      <TableCell>
                        <strong>{band.name}</strong>
                        {band.isFeatured && (
                          <StatusBadge
                            status="pending"
                            style={{ marginLeft: 10 }}
                          >
                            Istaknuto
                          </StatusBadge>
                        )}
                      </TableCell>
                      <TableCell>{band.genre || "N/A"}</TableCell>
                      <TableCell>{band.city || "N/A"}</TableCell>
                      <TableCell>{band.founded || "N/A"}</TableCell>
                      <TableCell>
                        <StatusBadge $active={band.active}>
                          {band.active ? (
                            <>
                              <FiCheckCircle /> Aktivan
                            </>
                          ) : (
                            <>
                              <FiXCircle /> Neaktivan
                            </>
                          )}
                        </StatusBadge>
                      </TableCell>

                      {/* Dashboard Pristup */}
                      <TableCell>
                        <CenteredContainer>
                          {dashboardStatus?.username ? (
                            <>
                              <StatusBadge
                                $active={true}
                                status="active"
                                style={{ width: "fit-content" }}
                              >
                                <FiUserCheck /> Ima pristup
                              </StatusBadge>
                              <div
                                style={{ fontSize: "0.8rem", color: "#888" }}
                              >
                                Username: {dashboardStatus?.username}
                              </div>
                              <div style={{ display: "flex", gap: "0.25rem" }}>
                                <ActionButton
                                  variant="info"
                                  size="small"
                                  onClick={() =>
                                    openModal("resetPassword", band)
                                  }
                                  disabled={dashboardAccess.resetLoading}
                                  title="Reset Password"
                                >
                                  <FiLock size={16} />
                                </ActionButton>
                                <ActionButton
                                  variant="danger"
                                  size="small"
                                  onClick={() => handleRemoveAccess(band.id)}
                                  disabled={dashboardAccess.removeLoading}
                                  title="Ukloni Pristup"
                                >
                                  <FiUserX size={16} />
                                </ActionButton>
                              </div>
                            </>
                          ) : (
                            <>
                              <StatusBadge
                                status="inactive"
                                style={{ width: "fit-content" }}
                              >
                                <FiUserX size={16} /> Nema pristup
                              </StatusBadge>
                              <ActionButton
                                variant="success"
                                style={{ width: "fit-content" }}
                                size="small"
                                onClick={() => openModal("createAccess", band)}
                                disabled={dashboardAccess.createLoading}
                              >
                                <FiKey size={16} /> Kreiraj Pristup
                              </ActionButton>
                            </>
                          )}
                        </CenteredContainer>
                      </TableCell>

                      <TableCell>
                        <Buttons>
                          <ActionButton
                            variant="secondary"
                            onClick={() => openModal("editBand", band)}
                            title="Izmeni"
                          >
                            <FiEdit size={16} />
                          </ActionButton>

                          <ActionButton
                            variant="info"
                            onClick={() =>
                              navigate(`/admin-panel/bend/${band.id}`)
                            }
                            title="Upravljaj detaljima"
                          >
                            <FiSettings size={16} />
                          </ActionButton>

                          <ActionButton
                            variant={band.active ? "danger" : "success"}
                            onClick={() => handleToggleStatus(band.id)}
                            title={band.active ? "Deaktiviraj" : "Aktiviraj"}
                          >
                            <FiPower size={16} />
                          </ActionButton>

                          <ActionButton
                            variant="danger"
                            onClick={() => handleDeleteBand(band.id)}
                            title="Obriši"
                          >
                            <FiTrash2 size={16} />
                          </ActionButton>
                        </Buttons>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </tbody>
          </Table>
          <PaginationContainer>
            <PaginationInfo>
              Prikazuje se {filteredBands.length} od {pagination?.totalBands}{" "}
              benda
            </PaginationInfo>

            <div>
              <PaginationButton
                onClick={goToPreviousPage}
                disabled={currentPage === 1 || loading}
              >
                <FiChevronLeft />
              </PaginationButton>

              {Array.from(
                { length: pagination?.totalPages },
                (_, i) => i + 1
              ).map((page) => (
                <PaginationButton
                  key={page}
                  onClick={() => goToPage(page)}
                  $active={currentPage === page}
                  disabled={loading}
                >
                  {page}
                </PaginationButton>
              ))}

              <PaginationButton
                onClick={goToNextPage}
                disabled={currentPage === pagination?.totalPages || loading}
              >
                <FiChevronRight />
              </PaginationButton>
            </div>
          </PaginationContainer>
        </TableContainer>

        {/* BAND MODAL */}
        <BandModal
          activeModal={activeModal}
          closeModal={closeModal}
          selectedBand={selectedBand}
          handleBandSubmit={handleBandSubmit}
        />

        {/* DASHBOARD ACCESS MODALS */}
        <DashboardAccessModals
          activeModal={activeModal}
          closeModal={closeModal}
          selectedBand={selectedBand}
          adminUsername={adminCredentials[selectedBand?.id]?.username}
          onCreateAccess={handleCreateDashboardAccess}
          onResetPassword={handleResetPassword}
          loading={
            dashboardAccess.createLoading ||
            dashboardAccess.resetLoading ||
            dashboardAccess.removeLoading
          }
        />

        {/* CONFIRMATION MODAL */}
        {/* {confirmModal.isOpen && (
          <ConfirmModal>
            <div className="modal-content">
              <h3>{confirmModal.title}</h3>
              <p>{confirmModal.message}</p>
              <div className="modal-actions">
                <ActionButton
                  variant="secondary"
                  onClick={closeConfirmModal}
                  className="cancel-btn"
                >
                  {confirmModal.cancelText}
                </ActionButton>
                <ActionButton
                  variant="danger"
                  onClick={handleConfirm}
                  className="confirm-btn"
                >
                  {confirmModal.confirmText}
                </ActionButton>
              </div>
            </div>
          </ConfirmModal>
        )} */}

        {/* Delete Confirmation Modal */}
        <AnimatePresence>
          {confirmModal.isOpen && (
            <ModalOverlay
              as={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeConfirmModal}
            >
              <ConfirmBox
                as={motion.div}
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => e.stopPropagation()}
              >
                <CloseBtn onClick={closeConfirmModal}>
                  <FaTimes />
                </CloseBtn>
                <h3>{confirmModal.title}</h3>
                <p>{confirmModal.message}</p>
                <ModalActions>
                  <Cancel onClick={closeConfirmModal}>
                    {confirmModal.cancelText}
                  </Cancel>

                  <Confirm onClick={handleConfirm}>
                    {confirmModal.confirmText}
                  </Confirm>
                </ModalActions>
              </ConfirmBox>
            </ModalOverlay>
          )}
        </AnimatePresence>
      </PageContainer>
    </AdminContainer>
  );
};

export default AdminBandsPage;
